import {
  FormControl,
  FormErrorMessage,
  FormLabel,
  Icon,
  Input,
  InputGroup,
  InputRightElement,
  Link,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { yupResolver } from "@hookform/resolvers/yup";
import { signInWithEmailAndPassword } from "firebase/auth";
import { TFunction } from "i18next";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import * as Yup from "yup";
import { auth } from "../../auth/config";
import { Routes } from "../../helpers/router";
import { Button } from "../../components/chackra-ui-button";
import * as Sentry from "@sentry/react";
import { useState } from "react";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";

type FormValues = {
  email: string;
  password: string;
};

const validationSchema = (t: TFunction) =>
  Yup.object({
    email: Yup.string().required(t("login.email.emailRequired")),
    password: Yup.string().required(t("login.email.passwordRequired")),
  });

export const LoginEmailForm = () => {
  const toast = useToast();
  const [showPassword, setShowPassword] = useState(false);
  const { t } = useTranslation();

  const {
    register,
    handleSubmit,
    formState: { isSubmitting, errors },
  } = useForm<FormValues>({
    resolver: yupResolver(validationSchema(t)),
  });

  const onSubmit = handleSubmit(
    async ({ email, password }) => {
      try {
        await signInWithEmailAndPassword(auth, email, password);
      } catch (error: any) {
        toast({
          title: t("login.errors.invalidCredentials"),
          description: t("login.errors.checkCredentials"),
          status: "error",
          duration: 9000,
          isClosable: true,
        });
        Sentry.captureException(error);
      }
    },
    (errors, event) => {
      Sentry.captureException(new Error("Unable to submit login form"), {
        extra: { event, errors },
      });
    }
  );

  return (
    <form onSubmit={onSubmit} style={{ width: "100%" }} autoComplete="off">
      <VStack spacing={4} w="full">
        <FormControl isInvalid={!!errors.email}>
          <FormLabel fontSize="16px" fontWeight={600} color="slate.600">
            {t("login.email.emailLabel")}
          </FormLabel>
          <Input
            id="email"
            type="email"
            autoComplete="off"
            placeholder={t("login.email.emailPlaceholder", "you@email.com")}
            {...register("email")}
          />
          {errors.email?.message && (
            <FormErrorMessage>{errors.email.message}</FormErrorMessage>
          )}
        </FormControl>

        <FormControl isInvalid={!!errors.password}>
          <FormLabel fontSize="16px" fontWeight={600} color="slate.600">
            {t("login.email.passwordLabel")}
          </FormLabel>
          <InputGroup>
            <Input
              id="password"
              type={showPassword ? "text" : "password"}
              autoComplete="new-password"
              pr="48px"
              {...register("password")}
            />
            <InputRightElement h="full" pr={1}>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowPassword(!showPassword)}
                minW="36px"
                h="36px"
                borderRadius="full"
              >
                <Icon boxSize={5} color="slate.400">
                  {showPassword ? <EyeSlashIcon /> : <EyeIcon />}
                </Icon>
              </Button>
            </InputRightElement>
          </InputGroup>
          {errors.password?.message && (
            <FormErrorMessage>{errors.password.message}</FormErrorMessage>
          )}
        </FormControl>

        <Link
          as={RouterLink}
          to={Routes.ResetPassword()}
          color="#0078FF"
          fontSize="16px"
          fontWeight={500}
          alignSelf="flex-start"
          mt={1}
        >
          {t("login.email.forgotPassword")}
        </Link>

        <Button
          type="submit"
          variant="senseiFilled"
          w="full"
          isLoading={isSubmitting}
          mt={3}
        >
          {t("login.email.loginButton")}
        </Button>
      </VStack>
    </form>
  );
};
